import Resources from './CorporateViewTabs/Resources';
import Notifications from './CorporateViewTabs/Notifications';
import AORs from './CorporateViewTabs/AorsTab/AORs';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import ReportsView from 'components/Reports/ReportsView';
import { LayersView } from './CorporateViewTabs/LayersTab/LayersView';
import OrgActionsPage from 'components/IAP/OrgActionsPage';
import IncidentActionsPage from 'components/IAP/IncidentActionsPage';
import IncidentWorkspaceTabBar from 'components/IAP/IncidentWorkspaceTabBar';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useUscgMapContext } from './CorporateViewTabs/LayersTab/UscgMapContext';
import {
  resolveSitlCopTourSeatHref,
  SITL_COP_TOUR_PARAM,
} from './sitlCopTour/sitlCopTourSteps';
import { registerFocFsltpTourApi } from './focFsltpTour/focFsltpTourBridge';
import { registerFocNotificationsTourApi } from './focNotificationsTour/focNotificationsTourBridge';
import { FOC_NOTIFICATIONS_TOUR_HREF } from './focNotificationsTour/focNotificationsTourSteps';
import { registerFocIncidentsTourApi } from './focIncidentsTour/focIncidentsTourBridge';
import {
  FOC_INCIDENTS_TOUR_HREF,
  FOC_INCIDENTS_TOUR_WIZARD_HREF,
} from './focIncidentsTour/focIncidentsTourSteps';
import { registerFocIncidentRosterTourApi } from './focIncidentRosterTour/focIncidentRosterTourBridge';
import {
  ensureFocIncidentRosterDemoWorkspace,
  FOC_DEMO_INCIDENT_ROSTER_NAME,
  focIncidentRosterDemoHref,
} from './focIncidentRosterTour/focIncidentRosterDemo';
import {
  FOC_INCIDENT_ROSTER_TOUR_PARAM,
  resolveFocIncidentRosterHref,
} from './focIncidentRosterTour/focIncidentRosterTourSteps';
import {
  ensureFocInitialResponseDemoWorkspace,
  FOC_DEMO_INITIAL_RESPONSE_NAME,
  focInitialResponseDemoHref,
} from './focInitialResponseTour/focInitialResponseDemo';
import { FOC_INITIAL_RESPONSE_TOUR_PARAM } from './focInitialResponseTour/focInitialResponseTourSteps';
import {
  ensureFocTransitionDemoWorkspace,
  FOC_DEMO_TRANSITION_NAME,
  focTransitionDemoHref,
} from './focTransitionTour/focTransitionDemo';
import { FOC_TRANSITION_TOUR_PARAM } from './focTransitionTour/focTransitionTourSteps';
import {
  ensureFocPlanningPDemoWorkspace,
  FOC_DEMO_PLANNING_P_NAME,
  focPlanningPDemoHref,
} from './focPlanningPTour/focPlanningPDemo';
import {
  findFocPlanningPTourEntryByParam,
  FOC_PLANNING_P_TOUR_ENTRIES,
} from './focPlanningPTour/focPlanningPTourRegistry';
import {
  FOC_WS_TOUR_PARAM,
  focWsTourSeatHref,
} from './focWsTour/focWsTourSteps';
import {
  findFocCmdTourEntryByParam,
  focCmdTourSeatHref,
} from './focCmdTour/focCmdTourRegistry';
import {
  FOC_RES_211_TOUR_PARAM,
  focRes211TourIapHref,
} from './focResTour/focRes211TourSteps';
import {
  FOC_RES_214_TOUR_PARAM,
  focRes214TourIapHref,
} from './focResTour/focRes214TourSteps';
import {
  FOC_DMOB_TOUR_PARAM,
  focDmobTourSeatHref,
} from './focDmobTour/focDmobTourSteps';
import {
  findFocPlnTourEntryByParam,
  focPlnTourSeatHref,
} from './focPlnTour/focPlnTourRegistry';
import {
  FOC_OPS_TOUR_PARAM,
  focOpsTourSeatHref,
} from './focOpsTour/focOpsTourSteps';
import {
  FOC_LOG_TOUR_PARAM,
  focLogTourSeatHref,
} from './focLogTour/focLogTourSteps';
import {
  FOC_EXT_TOUR_PARAM,
  focExtTourRosterHref,
} from './focExtTour/focExtTourSteps';
import {
  FOC_AI_FOC_TOUR_PARAM,
} from './focAiTour/focAiFocTourSteps';
import {
  FOC_AI_IOC_TOUR_PARAM,
} from './focAiTour/focAiIocTourSteps';
import { findFocAiTourEntryByParam } from './focAiTour/focAiTourRegistry';
import { registerFocAiTourApi } from './focAiTour/focAiTourBridge';
import {
  focFsltpHubDailyHref,
  focFsltpLayersHref,
  focFsltpSitlPresentationHref,
} from './focFsltpTour/focFsltpTourSteps';
import { useChatSidebar } from 'components/GlobalChatSidebar/ChatSidebarProvider';
import { isSitlSeatPath } from './utils/mapExclusiveSurfaces';
import {
  selectGroupGuid,
  selectIncidentGuid,
  selectUser,
  useAppSelector,
} from 'slices/commonSelectors';
import { toast } from 'react-toastify';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SearchableNavMenu } from './SearchableNavMenu';
import {
  useHighSecurityFlag,
  useHubOnlyFlag,
  useHwcgFlag,
  useUscgFocRequirementsFlag,
  useHubChromeEnabled,
  useNotificationCenterFlag,
} from 'utils/featureFlags';
import { UPGRADE_TO_ENABLE } from 'components/Notifications/LockedUpgradeOption';
import { useAorText } from 'utils/aorTerminology';
import {
  CorporateViewTabs,
  HUB_MORE_TABS,
  HUB_PRIMARY_TABS,
  HUB_TAB_ICONS,
  INCIDENT_MORE_TABS,
  TabNameToIconName,
  moreTabLabel,
} from './hubNavTabs';
import { Button } from '@/components/ui/button';
import { CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { Bell, Calendar, ChevronLeft, ListChecks, Siren } from 'lucide-react';
import Incidents from './CorporateViewTabs/IncidentsTab/Incidents';
import EventsView from './CorporateViewTabs/EventsTab/EventsView';
import { SitStatView } from './CorporateViewTabs/SitStatTab/SitStatView';
import { PlanLibraryView } from './CorporateViewTabs/PlanLibraryTab/PlanLibraryView';
import { RosterTemplateLibraryView } from './CorporateViewTabs/RosterTemplatesTab/RosterTemplateLibraryView';
import { PresentationPage } from 'components/IAP/presentation/PresentationPage';
import { NotesView } from 'components/Notes/NotesView';
import { HwcgContactListsView } from './CorporateViewTabs/ContactListsTab/HwcgContactListsView';
import { FocRequirementsTrackerView } from './CorporateViewTabs/FocTrackerTab/FocRequirementsTrackerView';
import { useOrgEvents } from 'hooks/orgEvents/orgEventHooks';
import { useUserNotifications } from 'hooks/useUserNotifications';
import { countUnseenOrgEvents } from './CorporateViewTabs/EventsTab/eventsSeenState';
import { useEventsSeenState } from './CorporateViewTabs/EventsTab/useEventsSeenState';
import { isHardcodedMemphisThreatNotification } from './CorporateViewTabs/NotificationsTab/memphisThreatNotification';
import { useMapSidebarCollapse } from './MapSidebarComponents';
import { requestDraftLayerLeave } from './CorporateViewTabs/LayersTab/draftLayerLeaveGuard';
import { CREATE_INTENT_PARAM, type CreateIntentKind } from './useCreateIntent';
import { HubTourAutoStart } from './hubTour/HubTourAutoStart';
import { registerIncidentLifecycleTourApi } from 'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTourBridge';
import {
  INCIDENT_TOUR_PARAM,
  wantsIncidentTour,
} from 'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTourSteps';
import { registerHwcgTourApi } from 'components/IncidentConfig/hwcgTour/hwcgTourBridge';
import {
  HWCG_TOUR_PARAM,
  wantsHwcgTour,
} from 'components/IncidentConfig/hwcgTour/hwcgTourSteps';
import { registerFocPlanningTourApi } from './focPlanningTour/focPlanningTourBridge';
import {
  FOC_PLANNING_TOUR_PARAM,
  resolveFocPlanningRosterHref,
  resolveFocPlanningTasksHref,
  wantsFocPlanningTour,
} from './focPlanningTour/focPlanningTourSteps';
import {
  useAllIncidents,
  useCreateIncident,
} from 'components/IAP/hooks/incidentHooks';

export { CorporateViewTabs, TabNameToIconName } from './hubNavTabs';

type CorporateViewTabGroupProps = {
  ics?: boolean;
  currentMode: string;
  updateMode: (isFutureMode: string) => void;
  currentPhase: number;
};

export const CorporateViewTabGroup = ({ ics }: CorporateViewTabGroupProps) => {
  const highSecurityFlag = useHighSecurityFlag();
  const hwcg = useHwcgFlag();
  const uscgFoc = useUscgFocRequirementsFlag();
  const hubChrome = useHubChromeEnabled();
  const notificationCenter = useNotificationCenterFlag();
  const aorText = useAorText();
  const hideNotificationsTab = notificationCenter && !ics;
  const sidebarCollapse = useMapSidebarCollapse();
  const navigate = useNavigate();
  const { incidentId } = useParams<{ incidentId: string }>();
  const { data: userNotifications = [] } = useUserNotifications();
  const { lastSeenAt, markSeen } = useEventsSeenState();
  const { data: orgEvents = [] } = useOrgEvents({
    enabled: hideNotificationsTab,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = hideNotificationsTab
    ? CorporateViewTabs.Events
    : CorporateViewTabs.Notifications;
  const currentTab = useMemo(
    () => (searchParams.get('openTab') as CorporateViewTabs) || defaultTab,
    [searchParams, defaultTab]
  );

  const setCurrentTab = useCallback(
    (newCurrentTab: CorporateViewTabs) => {
      const apply = () => {
        if (
          hideNotificationsTab &&
          newCurrentTab === CorporateViewTabs.Events
        ) {
          markSeen();
        }
        setSearchParams(
          (searchParams) => {
            searchParams.set('openTab', newCurrentTab);
            return searchParams;
          },
          { replace: true }
        );
      };
      if (newCurrentTab === currentTab) {
        apply();
        return;
      }
      requestDraftLayerLeave(apply);
    },
    [currentTab, hideNotificationsTab, markSeen, setSearchParams]
  );

  const openTabAndCreate = useCallback(
    (tab: CorporateViewTabs, kind: CreateIntentKind) => {
      requestDraftLayerLeave(() => {
        setSearchParams(
          (params) => {
            params.set('openTab', tab);
            params.set(CREATE_INTENT_PARAM, kind);
            return params;
          },
          { replace: true }
        );
      });
    },
    [setSearchParams]
  );

  const { isMapVisible } = useUscgMapContext();

  // Incident Lifecycle tour: the Hub owns the first phase, then hands the
  // walkthrough to the create route through the URL.
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const wantsIncidentTourHub =
    !ics && wantsIncidentTour(searchParams.get(INCIDENT_TOUR_PARAM), 'hub');
  const incidentTourStartedRef = useRef(false);
  const wantsHwcgLibrary =
    !ics && hwcg && wantsHwcgTour(searchParams.get(HWCG_TOUR_PARAM), 'library');
  const hwcgTourStartedRef = useRef(false);
  const focPlanningParam = searchParams.get(FOC_PLANNING_TOUR_PARAM);
  const wantsFocLibrary =
    !ics && uscgFoc && wantsFocPlanningTour(focPlanningParam, 'library');
  const wantsFocPackages =
    !ics && uscgFoc && wantsFocPlanningTour(focPlanningParam, 'packages');
  const wantsFocRosterTemplates =
    !ics &&
    uscgFoc &&
    wantsFocPlanningTour(focPlanningParam, 'rosterTemplates');
  const focLibraryStartedRef = useRef(false);
  const focPackagesStartedRef = useRef(false);
  const focRosterTemplatesStartedRef = useRef(false);
  const allIncidents = useAllIncidents();
  const createIncident = useCreateIncident();
  const currentIncidentGuid = useAppSelector(selectIncidentGuid);
  const groupGuid = useAppSelector(selectGroupGuid);
  const user = useAppSelector(selectUser);
  const focTasksHref = resolveFocPlanningTasksHref(allIncidents.data ?? []);
  const focRosterHref = resolveFocPlanningRosterHref(allIncidents.data ?? []);
  const focTasksHrefRef = useRef(focTasksHref);
  focTasksHrefRef.current = focTasksHref;
  const focRosterHrefRef = useRef(focRosterHref);
  focRosterHrefRef.current = focRosterHref;
  const focIncidentRosterHref = resolveFocIncidentRosterHref(
    allIncidents.data ?? [],
    currentIncidentGuid
  );
  const focIncidentRosterHrefRef = useRef(focIncidentRosterHref);
  focIncidentRosterHrefRef.current = focIncidentRosterHref;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const createIncidentRef = useRef(createIncident);
  createIncidentRef.current = createIncident;
  const groupGuidRef = useRef(groupGuid);
  groupGuidRef.current = groupGuid;
  const userRef = useRef(user);
  userRef.current = user;
  const wantsSitlCopTourHub =
    !ics && uscgFoc && searchParams.get(SITL_COP_TOUR_PARAM) === '1';
  const sitlCopBootstrapRef = useRef(false);
  const wantsFocIncidentRosterTourHub =
    !ics &&
    uscgFoc &&
    searchParams.get(FOC_INCIDENT_ROSTER_TOUR_PARAM) === '1';
  const focIncidentRosterBootstrapRef = useRef(false);
  const wantsFocInitialResponseTourHub =
    !ics &&
    uscgFoc &&
    searchParams.get(FOC_INITIAL_RESPONSE_TOUR_PARAM) === '1';
  const focInitialResponseBootstrapRef = useRef(false);
  const wantsFocTransitionTourHub =
    !ics &&
    uscgFoc &&
    searchParams.get(FOC_TRANSITION_TOUR_PARAM) === '1';
  const focTransitionBootstrapRef = useRef(false);
  const focPlanningPTourEntry = !ics && uscgFoc
    ? findFocPlanningPTourEntryByParam(searchParams)
    : null;
  const wantsFocPlanningPTourHub = Boolean(focPlanningPTourEntry);
  const focPlanningPBootstrapRef = useRef(false);
  const wantsFocWsTourHub =
    !ics && uscgFoc && searchParams.get(FOC_WS_TOUR_PARAM) === '1';
  const focWsBootstrapRef = useRef(false);
  const focCmdTourEntry =
    !ics && uscgFoc ? findFocCmdTourEntryByParam(searchParams) : null;
  const wantsFocCmdTourHub = Boolean(focCmdTourEntry);
  const focCmdBootstrapRef = useRef(false);
  const wantsFocRes211TourHub =
    !ics && uscgFoc && searchParams.get(FOC_RES_211_TOUR_PARAM) === '1';
  const focRes211BootstrapRef = useRef(false);
  const wantsFocRes214TourHub =
    !ics && uscgFoc && searchParams.get(FOC_RES_214_TOUR_PARAM) === '1';
  const focRes214BootstrapRef = useRef(false);
  const focPlnTourEntry =
    !ics && uscgFoc ? findFocPlnTourEntryByParam(searchParams) : null;
  const wantsFocPlnTourHub = Boolean(focPlnTourEntry);
  const focPlnBootstrapRef = useRef(false);
  const wantsFocOpsTourHub =
    !ics && uscgFoc && searchParams.get(FOC_OPS_TOUR_PARAM) === '1';
  const focOpsBootstrapRef = useRef(false);
  const wantsFocLogTourHub =
    !ics && uscgFoc && searchParams.get(FOC_LOG_TOUR_PARAM) === '1';
  const focLogBootstrapRef = useRef(false);
  const wantsFocDmobTourHub =
    !ics && uscgFoc && searchParams.get(FOC_DMOB_TOUR_PARAM) === '1';
  const focDmobBootstrapRef = useRef(false);
  const wantsFocExtTourHub =
    !ics && uscgFoc && searchParams.get(FOC_EXT_TOUR_PARAM) === '1';
  const focExtBootstrapRef = useRef(false);
  const focAiTourEntry =
    !ics && uscgFoc ? findFocAiTourEntryByParam(searchParams) : null;
  const wantsFocAiTourHub = Boolean(focAiTourEntry);
  const focAiTourStartedRef = useRef(false);
  const { show: showAiSidebar, setAiPanel } = useChatSidebar();

  useEffect(() => {
    if (ics) return;
    return registerIncidentLifecycleTourApi({
      openCreateMenu: () => setCreateMenuOpen(true),
      navigateTo: (href) => navigateRef.current(href),
    });
  }, [ics]);

  useEffect(() => {
    if (ics) return;
    return registerHwcgTourApi({
      navigateTo: (href) => navigateRef.current(href),
    });
  }, [ics]);

  useEffect(() => {
    if (!wantsIncidentTourHub || incidentTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (incidentTourStartedRef.current) return;
      incidentTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(INCIDENT_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import(
        'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTour'
      )
        .then(({ startIncidentLifecycleTour }) => {
          startIncidentLifecycleTour('hub');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsIncidentTourHub, setSearchParams]);

  // 5.3 SITL COP: hub Product Tours entry → Situation Unit seat on a live incident.
  useEffect(() => {
    if (!wantsSitlCopTourHub) {
      sitlCopBootstrapRef.current = false;
      return;
    }
    if (sitlCopBootstrapRef.current || allIncidents.isLoading) return;

    const href = resolveSitlCopTourSeatHref(
      currentIncidentGuid ?? undefined,
      allIncidents.data ?? []
    );
    if (!href) return;

    sitlCopBootstrapRef.current = true;
    setSearchParams(
      (params) => {
        params.delete(SITL_COP_TOUR_PARAM);
        return params;
      },
      { replace: true }
    );
    navigateRef.current(href);
  }, [
    wantsSitlCopTourHub,
    allIncidents.isLoading,
    allIncidents.data,
    currentIncidentGuid,
    setSearchParams,
  ]);

  useEffect(() => {
    if (ics) return;
    return registerFocPlanningTourApi({
      navigateTo: (href) => navigateRef.current(href),
      resolveTasksHref: () => focTasksHrefRef.current,
      resolveRosterHref: () => focRosterHrefRef.current,
    });
  }, [ics]);

  useEffect(() => {
    if (!wantsFocLibrary) {
      focLibraryStartedRef.current = false;
      return;
    }
    if (focLibraryStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (focLibraryStartedRef.current) return;
      focLibraryStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_PLANNING_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('./focPlanningTour/focPlanningTour')
        .then(({ startFocPlanningTour }) => {
          startFocPlanningTour('library');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsFocLibrary, setSearchParams]);

  useEffect(() => {
    if (!wantsFocRosterTemplates) {
      focRosterTemplatesStartedRef.current = false;
      return;
    }
    if (focRosterTemplatesStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (focRosterTemplatesStartedRef.current) return;
      focRosterTemplatesStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_PLANNING_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('./focPlanningTour/focPlanningTour')
        .then(({ startFocPlanningTour }) => {
          startFocPlanningTour('rosterTemplates');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsFocRosterTemplates, setSearchParams]);

  useEffect(() => {
    if (!wantsFocPackages) {
      focPackagesStartedRef.current = false;
      return;
    }
    if (focPackagesStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (focPackagesStartedRef.current) return;
      focPackagesStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_PLANNING_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('./focPlanningTour/focPlanningTour')
        .then(({ startFocPlanningTour }) => {
          startFocPlanningTour('packages');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsFocPackages, setSearchParams]);

  useEffect(() => {
    if (!wantsHwcgLibrary || hwcgTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (hwcgTourStartedRef.current) return;
      hwcgTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(HWCG_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/IncidentConfig/hwcgTour/hwcgTour')
        .then(({ startHwcgTour }) => {
          startHwcgTour('library', true);
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsHwcgLibrary, setSearchParams]);

  const showCfaCollapseTrigger =
    hubChrome && isMapVisible && !!sidebarCollapse?.isMainSidebarVisible;

  const activeNotificationCount = useMemo(() => {
    return userNotifications.filter(
      (n) =>
        !n.archived &&
        (isHardcodedMemphisThreatNotification(n.notificationId) ||
          !incidentId ||
          n.incidentId === incidentId)
    ).length;
  }, [userNotifications, incidentId]);

  const unseenEventCount = useMemo(
    () =>
      hideNotificationsTab ? countUnseenOrgEvents(orgEvents, lastSeenAt) : 0,
    [hideNotificationsTab, orgEvents, lastSeenAt]
  );

  useEffect(() => {
    if (!hideNotificationsTab) return;
    if (currentTab === CorporateViewTabs.Events) {
      markSeen();
    }
  }, [hideNotificationsTab, currentTab, markSeen]);

  const moreTabs = useMemo(() => {
    const source = ics ? INCIDENT_MORE_TABS : HUB_MORE_TABS;
    return source.filter((tab) => {
      if (tab === CorporateViewTabs.Actions && highSecurityFlag) return false;
      if (tab === CorporateViewTabs.ContactLists && (ics || !hwcg))
        return false;
      if (tab === CorporateViewTabs.FocTracker && (ics || !uscgFoc))
        return false;
      return true;
    });
  }, [ics, highSecurityFlag, hwcg, uscgFoc]);

  const isMoreTabActive = useMemo(
    () => moreTabs.includes(currentTab),
    [currentTab, moreTabs]
  );

  const moreTriggerLabel = isMoreTabActive
    ? `More · ${moreTabLabel(currentTab, aorText('AORs'))}`
    : 'More';

  const cfaNotificationsChrome =
    hubChrome &&
    (currentTab === CorporateViewTabs.Notifications ||
      currentTab === CorporateViewTabs.Events ||
      currentTab === CorporateViewTabs.Layers ||
      currentTab === CorporateViewTabs.Incidents);

  // Creation is per-tab, so the Hub's single menu either routes (incident) or
  // switches to the owning tab and asks it to open its own form.
  const hubOnly = useHubOnlyFlag();

  const createItems = useMemo(() => {
    const items: {
      id: string;
      label: string;
      icon: ReactNode;
      tour?: string;
      lockTooltip?: string;
      run: () => void;
    }[] = [
      {
        id: 'incident',
        label: 'Create Incident',
        icon: <Siren className="tw:size-4" />,
        tour: 'hub-create-incident',
        lockTooltip: hubOnly ? UPGRADE_TO_ENABLE : undefined,
        run: () => {
          if (hubOnly) return;
          navigate('/create/incident');
        },
      },
      {
        id: 'notification',
        label: 'Create Notification',
        icon: <Bell className="tw:size-4" />,
        run: () =>
          openTabAndCreate(CorporateViewTabs.Notifications, 'notification'),
      },
    ];

    items.push({
      id: 'event',
      label: 'Create Event',
      icon: <Calendar className="tw:size-4" />,
      run: () => openTabAndCreate(CorporateViewTabs.Events, 'event'),
    });

    if (!highSecurityFlag) {
      items.push({
        id: 'action',
        label: 'Create Action',
        icon: <ListChecks className="tw:size-4" />,
        run: () => openTabAndCreate(CorporateViewTabs.Actions, 'action'),
      });
    }

    return items;
  }, [highSecurityFlag, hubOnly, navigate, openTabAndCreate]);

  // Incident map IA no longer surfaces Incidents/AORs/SitStat (or Actions under high security).
  // Events stays available as a primary tab. SITL seat COP mode keeps SitStat.
  const location = useLocation();
  const sitlSeatCop = isSitlSeatPath(location.pathname);

  useEffect(() => {
    return registerFocFsltpTourApi({
      getLocation: () => ({
        pathname: location.pathname,
        search: location.search,
      }),
      showLayers: () => navigateRef.current(focFsltpLayersHref()),
      openHubDaily: () => navigateRef.current(focFsltpHubDailyHref()),
      openSitlPresentation: () => {
        const href = focFsltpSitlPresentationHref(
          currentIncidentGuid ?? undefined,
          allIncidents.data ?? []
        );
        if (href) navigateRef.current(href);
      },
    });
  }, [allIncidents.data, currentIncidentGuid, location.pathname, location.search]);

  useEffect(() => {
    return registerFocNotificationsTourApi({
      showNotifications: () => {
        if (location.pathname.includes('/hub') || location.pathname === '/') {
          setCurrentTab(CorporateViewTabs.Notifications);
          return;
        }
        navigateRef.current(FOC_NOTIFICATIONS_TOUR_HREF);
      },
    });
  }, [location.pathname, setCurrentTab]);

  useEffect(() => {
    return registerFocIncidentsTourApi({
      getLocation: () => ({
        pathname: location.pathname,
        search: location.search,
      }),
      showIncidents: () => {
        if (location.pathname.includes('/hub') || location.pathname === '/') {
          setCurrentTab(CorporateViewTabs.Incidents);
          return;
        }
        navigateRef.current(FOC_INCIDENTS_TOUR_HREF);
      },
      openMoreMenu: () => setMoreMenuOpen(true),
      closeMoreMenu: () => setMoreMenuOpen(false),
      openCreateMenu: () => setCreateMenuOpen(true),
      closeCreateMenu: () => setCreateMenuOpen(false),
      openCreateIncident: () => {
        setCreateMenuOpen(false);
        navigateRef.current(FOC_INCIDENTS_TOUR_WIZARD_HREF);
      },
    });
  }, [location.pathname, location.search, setCurrentTab]);

  useEffect(() => {
    return registerFocIncidentRosterTourApi({
      getLocation: () => ({
        pathname: location.pathname,
        search: location.search,
      }),
      navigateToRoster: () => {
        const href = focIncidentRosterHrefRef.current;
        if (href) navigateRef.current(href);
      },
    });
  }, [location.pathname, location.search]);

  // FOC 7.2: find or create named Full ICS demo, then open its roster.
  // Session ref stays set so create runs at most once (including after the
  // tour query param is cleared mid-flight).
  useEffect(() => {
    if (!wantsFocIncidentRosterTourHub) return;
    if (focIncidentRosterBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focIncidentRosterBootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const incidentId = await ensureFocIncidentRosterDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!incidentId) {
          toast.error(
            `Could not prepare “${FOC_DEMO_INCIDENT_ROSTER_NAME}”.`
          );
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_INCIDENT_ROSTER_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focIncidentRosterDemoHref(incidentId));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_INCIDENT_ROSTER_NAME}”.`);
      }
    })();
  }, [
    wantsFocIncidentRosterTourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 8.x: find or create named unescalated IR demo, then open New ICS 201.
  useEffect(() => {
    if (!wantsFocInitialResponseTourHub) return;
    if (focInitialResponseBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focInitialResponseBootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const incidentId = await ensureFocInitialResponseDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!incidentId) {
          toast.error(
            `Could not prepare “${FOC_DEMO_INITIAL_RESPONSE_NAME}”.`
          );
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_INITIAL_RESPONSE_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focInitialResponseDemoHref(incidentId));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_INITIAL_RESPONSE_NAME}”.`);
      }
    })();
  }, [
    wantsFocInitialResponseTourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 9.1: find or create Transition demo, then open IR (or Planning if already escalated).
  useEffect(() => {
    if (!wantsFocTransitionTourHub) return;
    if (focTransitionBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focTransitionBootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocTransitionDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_TRANSITION_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_TRANSITION_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(
          focTransitionDemoHref(result.incidentId, result.escalated)
        );
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_TRANSITION_NAME}”.`);
      }
    })();
  }, [
    wantsFocTransitionTourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 10.x: find or create escalated Planning P demo, then open matching phase.
  useEffect(() => {
    if (!wantsFocPlanningPTourHub || !focPlanningPTourEntry) return;
    if (focPlanningPBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focPlanningPBootstrapRef.current = true;
    const entry = focPlanningPTourEntry;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocPlanningPDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            for (const tour of FOC_PLANNING_P_TOUR_ENTRIES) {
              params.delete(tour.param);
            }
            return params;
          },
          { replace: true }
        );
        navigateRef.current(
          focPlanningPDemoHref(result.incidentId, entry.param, entry.phase)
        );
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
      }
    })();
  }, [
    wantsFocPlanningPTourHub,
    focPlanningPTourEntry,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 13.1: ensure Planning P demo, then open Planning Section Chief seat.
  useEffect(() => {
    if (!wantsFocWsTourHub) return;
    if (focWsBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focWsBootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocPlanningPDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_WS_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focWsTourSeatHref(result.incidentId));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
      }
    })();
  }, [
    wantsFocWsTourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 14.x: ensure Planning P demo, then open the matching Command staff seat.
  useEffect(() => {
    if (!wantsFocCmdTourHub || !focCmdTourEntry) return;
    if (focCmdBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focCmdBootstrapRef.current = true;
    const entry = focCmdTourEntry;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocPlanningPDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(entry.param);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focCmdTourSeatHref(result.incidentId, entry));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
      }
    })();
  }, [
    wantsFocCmdTourHub,
    focCmdTourEntry,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 20.3: ensure Planning P demo, then open IAP resources / ICS 211.
  useEffect(() => {
    if (!wantsFocRes211TourHub) return;
    if (focRes211BootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focRes211BootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocPlanningPDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_RES_211_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focRes211TourIapHref(result.incidentId));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
      }
    })();
  }, [
    wantsFocRes211TourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 20.4: ensure Planning P demo, then open IAP Activity Logs / ICS 214.
  useEffect(() => {
    if (!wantsFocRes214TourHub) return;
    if (focRes214BootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focRes214BootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocPlanningPDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_RES_214_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focRes214TourIapHref(result.incidentId));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
      }
    })();
  }, [
    wantsFocRes214TourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 15.x: ensure Planning P demo, then open the matching Planning unit seat.
  useEffect(() => {
    if (!wantsFocPlnTourHub || !focPlnTourEntry) return;
    if (focPlnBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focPlnBootstrapRef.current = true;
    const entry = focPlnTourEntry;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocPlanningPDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(entry.param);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focPlnTourSeatHref(result.incidentId, entry));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
      }
    })();
  }, [
    wantsFocPlnTourHub,
    focPlnTourEntry,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 16: ensure Planning P demo, then open Operations Section Chief seat.
  useEffect(() => {
    if (!wantsFocOpsTourHub) return;
    if (focOpsBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focOpsBootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocPlanningPDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_OPS_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focOpsTourSeatHref(result.incidentId));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
      }
    })();
  }, [
    wantsFocOpsTourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 17.1: ensure Planning P demo, then open Logistics Section Chief seat.
  useEffect(() => {
    if (!wantsFocLogTourHub) return;
    if (focLogBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focLogBootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocPlanningPDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_LOG_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focLogTourSeatHref(result.incidentId));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
      }
    })();
  }, [
    wantsFocLogTourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 21: ensure Planning P demo, then open Demobilization Unit seat.
  useEffect(() => {
    if (!wantsFocDmobTourHub) return;
    if (focDmobBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focDmobBootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const result = await ensureFocPlanningPDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!result) {
          toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_DMOB_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focDmobTourSeatHref(result.incidentId));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_PLANNING_P_NAME}”.`);
      }
    })();
  }, [
    wantsFocDmobTourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  // FOC 22: ensure roster demo, then open IAP roster with external-user tour.
  useEffect(() => {
    if (!wantsFocExtTourHub) return;
    if (focExtBootstrapRef.current) return;
    if (allIncidents.isLoading) return;

    focExtBootstrapRef.current = true;
    const incidents = allIncidents.data ?? [];

    void (async () => {
      try {
        const incidentId = await ensureFocIncidentRosterDemoWorkspace({
          incidents,
          groupGuid: groupGuidRef.current || '',
          user: {
            user_guid: userRef.current?.user_guid || '',
            name:
              userRef.current?.profile_settings?.userName ||
              userRef.current?.user_name ||
              userRef.current?.email_address ||
              '',
            email_address: userRef.current?.email_address || '',
          },
          createIncident: (payload) =>
            createIncidentRef.current.mutateAsync(payload),
        });
        if (!incidentId) {
          toast.error(
            `Could not prepare “${FOC_DEMO_INCIDENT_ROSTER_NAME}”.`
          );
          return;
        }
        setSearchParams(
          (params) => {
            params.delete(FOC_EXT_TOUR_PARAM);
            return params;
          },
          { replace: true }
        );
        navigateRef.current(focExtTourRosterHref(incidentId));
      } catch {
        toast.error(`Could not prepare “${FOC_DEMO_INCIDENT_ROSTER_NAME}”.`);
      }
    })();
  }, [
    wantsFocExtTourHub,
    allIncidents.isLoading,
    allIncidents.data,
    setSearchParams,
  ]);

  useEffect(() => {
    if (ics) return;
    return registerFocAiTourApi({
      openAiPanel: () => {
        setAiPanel('ai');
        showAiSidebar();
      },
      showReportsAi: () => {
        setCurrentTab(CorporateViewTabs.Reports);
        setAiPanel('ai');
        showAiSidebar();
      },
    });
  }, [ics, setAiPanel, showAiSidebar, setCurrentTab]);

  // FOC 23.x: open PRATUS AI drawer on hub, then start IOC or FOC AI tour.
  useEffect(() => {
    if (!wantsFocAiTourHub || !focAiTourEntry) {
      focAiTourStartedRef.current = false;
      return;
    }
    if (focAiTourStartedRef.current) return;

    const entry = focAiTourEntry;
    const timer = window.setTimeout(() => {
      if (focAiTourStartedRef.current) return;
      focAiTourStartedRef.current = true;
      setAiPanel('ai');
      showAiSidebar();
      setSearchParams(
        (params) => {
          params.delete(FOC_AI_IOC_TOUR_PARAM);
          params.delete(FOC_AI_FOC_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      const start =
        entry.key === 'ioc'
          ? import('./focAiTour/focAiIocTour').then(({ startFocAiIocTour }) =>
              startFocAiIocTour()
            )
          : import('./focAiTour/focAiFocTour').then(({ startFocAiFocTour }) =>
              startFocAiFocTour()
            );
      void start.catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    wantsFocAiTourHub,
    focAiTourEntry,
    setAiPanel,
    showAiSidebar,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!ics) return;
    const hideSitStat =
      currentTab === CorporateViewTabs.SitStat && !sitlSeatCop;
    const isHiddenPrimary =
      currentTab === CorporateViewTabs.Incidents ||
      currentTab === CorporateViewTabs.AORs ||
      hideSitStat ||
      (currentTab === CorporateViewTabs.Actions && highSecurityFlag);
    if (isHiddenPrimary) {
      setCurrentTab(CorporateViewTabs.Notifications);
    }
  }, [ics, currentTab, highSecurityFlag, setCurrentTab, sitlSeatCop]);

  // Hub: redirect away from Actions when high security hides it from More.
  useEffect(() => {
    if (ics) return;
    if (currentTab === CorporateViewTabs.Actions && highSecurityFlag) {
      setCurrentTab(defaultTab);
    }
  }, [ics, currentTab, highSecurityFlag, defaultTab, setCurrentTab]);

  useEffect(() => {
    if (currentTab !== CorporateViewTabs.ContactLists) return;
    if (ics || !hwcg) setCurrentTab(defaultTab);
  }, [ics, hwcg, currentTab, defaultTab, setCurrentTab]);

  useEffect(() => {
    if (currentTab !== CorporateViewTabs.FocTracker) return;
    if (ics || !uscgFoc) setCurrentTab(defaultTab);
  }, [ics, uscgFoc, currentTab, defaultTab, setCurrentTab]);

  const primaryTabs = useMemo(() => {
    if (!hideNotificationsTab) return HUB_PRIMARY_TABS;
    return HUB_PRIMARY_TABS.filter(
      (tab) => tab !== CorporateViewTabs.Notifications
    );
  }, [hideNotificationsTab]);

  const tabToView: Record<CorporateViewTabs, React.ReactNode> = {
    Notifications: <Notifications />,
    Events: <EventsView />,
    Reports: <ReportsView />,
    Incidents: <Incidents />,
    Actions: ics ? <IncidentActionsPage /> : <OrgActionsPage />,
    AORs: <AORs />,
    Resources: <Resources />,
    Layers: <LayersView />,
    SitStat: <SitStatView />,
    PlanLibrary: <PlanLibraryView />,
    RosterTemplates: <RosterTemplateLibraryView />,
    Presentation: <PresentationPage scope={ics ? 'incident' : 'hub'} />,
    Notes: <NotesView scope={ics && incidentId ? 'incident' : 'org'} />,
    ContactLists: <HwcgContactListsView />,
    FocTracker: <FocRequirementsTrackerView />,
  };

  const renderLegacyTabButton = (tabName: CorporateViewTabs) => (
    <OverlayTrigger
      key={tabName}
      placement="bottom"
      overlay={(props) => <Tooltip {...props}>{tabName}</Tooltip>}
    >
      <StylishNewButton
        className={
          'button--secondary tab' + (tabName === currentTab ? ' active' : '')
        }
        onClick={() => setCurrentTab(tabName)}
      >
        <SharedIcon iconName={TabNameToIconName[tabName]} size="sm" />
      </StylishNewButton>
    </OverlayTrigger>
  );

  const renderCfaTabButton = (tabName: CorporateViewTabs) => {
    const Icon = HUB_TAB_ICONS[tabName];
    const active = tabName === currentTab;
    const badgeCount =
      tabName === CorporateViewTabs.Notifications
        ? activeNotificationCount
        : tabName === CorporateViewTabs.Events
          ? unseenEventCount
          : 0;
    const showBadge = badgeCount > 0;
    const badgeLabel = badgeCount > 99 ? '99+' : String(badgeCount);

    return (
      <OverlayTrigger
        key={tabName}
        placement="bottom"
        overlay={(props) => <Tooltip {...props}>{tabName}</Tooltip>}
      >
        <Button
          type="button"
          size="icon-sm"
          variant={active ? 'default' : 'outline'}
          data-tour={!ics ? `hub-tab-${tabName.toLowerCase()}` : undefined}
          aria-label={showBadge ? `${tabName} (${badgeCount})` : tabName}
          aria-pressed={active}
          className={cn(
            'tw:relative',
            !active && 'tw:border-border tw:bg-transparent tw:text-foreground'
          )}
          onClick={() => setCurrentTab(tabName)}
        >
          {showBadge ? (
            <span className="tw:absolute tw:-right-1 tw:-top-1 tw:flex tw:h-4 tw:min-w-4 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-destructive tw:px-1 tw:text-[10px] tw:font-semibold tw:leading-none tw:text-destructive-foreground">
              {badgeLabel}
            </span>
          ) : null}
          <Icon className="tw:h-4 tw:w-4" />
        </Button>
      </OverlayTrigger>
    );
  };

  return (
    <div
      className={cn(
        'd-flex flex-column h-100 w-100',
        cfaNotificationsChrome &&
          'dark tw:-m-2 tw:bg-background tw:p-2 tw:text-foreground'
      )}
      style={{ minHeight: 0 }}
    >
      {!ics ? (
        <div
          className={cn(
            'd-flex justify-content-between align-items-center flex-shrink-0',
            hubChrome &&
              'dark tw:border-b tw:border-border tw:bg-background tw:px-2 tw:py-1.5 tw:text-foreground'
          )}
          style={
            hubChrome
              ? undefined
              : { borderBottom: '2px solid var(--text-weak)' }
          }
        >
          <div
            className="initial-response-stepper w-100"
            style={{
              borderTop: 'none',
              borderBottom: 'none',
              // SCSS sets background-dark (#222529); clear it so the CFA tab strip jet black shows through.
              ...(hubChrome ? { backgroundColor: 'transparent' } : {}),
            }}
          >
            <div className="d-flex align-items-center justify-content-start flex-wrap gap-2">
              {primaryTabs.map((tabName) =>
                hubChrome
                  ? renderCfaTabButton(tabName)
                  : renderLegacyTabButton(tabName)
              )}
              {moreTabs.length > 0 ? (
                <SearchableNavMenu
                  id="hub-map-more-tabs"
                  triggerLabel={moreTriggerLabel}
                  selectedId={isMoreTabActive ? currentTab : undefined}
                  triggerVariant={isMoreTabActive ? 'default' : 'outline'}
                  open={moreMenuOpen}
                  onOpenChange={setMoreMenuOpen}
                  items={moreTabs.map((tabName) => {
                    const Icon = HUB_TAB_ICONS[tabName];
                    return {
                      id: tabName,
                      label: moreTabLabel(tabName, aorText('AORs')),
                      icon: <Icon className="tw:size-4" />,
                      tour:
                        tabName === CorporateViewTabs.Incidents
                          ? 'hub-tab-incidents'
                          : undefined,
                    };
                  })}
                  onSelect={(item) =>
                    setCurrentTab(item.id as CorporateViewTabs)
                  }
                />
              ) : null}
              <SearchableNavMenu
                id="hub-create-menu"
                triggerLabel="+ Create"
                triggerVariant="default"
                open={createMenuOpen}
                onOpenChange={setCreateMenuOpen}
                items={createItems.map((item) => ({
                  id: item.id,
                  label: item.label,
                  icon: item.icon,
                  tour: item.tour,
                  lockTooltip: item.lockTooltip,
                }))}
                onSelect={(item) => {
                  createItems.find((create) => create.id === item.id)?.run();
                }}
              />
              {showCfaCollapseTrigger ? (
                <div className="tw:ml-auto tw:flex tw:items-center">
                  <CollapsibleTrigger
                    render={
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        aria-label="Collapse panel"
                      />
                    }
                  >
                    <ChevronLeft className="tw:h-4 tw:w-4" />
                  </CollapsibleTrigger>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <IncidentWorkspaceTabBar
          endSlot={
            showCfaCollapseTrigger ? (
              <CollapsibleTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label="Collapse panel"
                  />
                }
              >
                <ChevronLeft className="tw:h-4 tw:w-4" />
              </CollapsibleTrigger>
            ) : undefined
          }
        />
      )}
      {currentTab !== CorporateViewTabs.SitStat ? (
        <div
          className={cn(
            'corporate-view-content-map-visible tw:px-4 tw:py-2',
            cfaNotificationsChrome && 'dark tw:bg-background'
          )}
        >
          {tabToView[currentTab]}
        </div>
      ) : null}
      {!ics ? <HubTourAutoStart /> : null}
    </div>
  );
};
